import {
    createSIPUri,
    MediaParticipant,
    parseSIPUri,
    useClientUISettings,
    useSession,
    useSipClient,
} from "@dvrt/web-phone-client";
import { useEffect, useRef } from "react";
import { useSettings } from "../tools";
import { useVolumeMeter } from "../audio-level";
import { useInterval } from "@dvrt/react-tools";
import { hasSetsDiff } from "conference/tools";
import { useCurrentMeeting } from "../contexts/currentMeeting";
import { useDispatch, useStore } from "react-redux";
import { State } from "conference/types";
import { setSpeakerList } from "./redux";

export const useParticipantSpeakerCalculator = () => {
    const sipClient = useSipClient();
    const context = useSession();
    const clientUISettings = useClientUISettings();
    const isSelfSpeaking = useRef<boolean>(false);
    const settings = useSettings();
    const selfCname = createSIPUri(sipClient.sipSettings.auth);
    const store = useStore<State>();
    const dispatch = useDispatch();

    const audioInputDeviceId = settings.audio ? clientUISettings.selectedMediaDevices.audioinput : null;

    useVolumeMeter(selfCname ? audioInputDeviceId : null, (vol) => {
        isSelfSpeaking.current = vol > 30;
    });

    useEffect(() => {
        if (!settings.audio) isSelfSpeaking.current = false;
    }, [settings.audio]);

    useInterval(() => {
        if (!sipClient.session) return;
        const receivers = sipClient.session.connection.getReceivers();
        const ids: string[] = [];
        for (const recv of receivers) {
            if (recv.track.kind !== "audio") continue;
            const now = new Date().getTime();
            const contributingSources = recv.getContributingSources();
            for (const csrc of contributingSources) {
                if (csrc.source === 0) continue;
                for (const p of context.participants) {
                    if (p.csrc === csrc.source) {
                        if (now - csrc.timestamp < 100) {
                            if (!ids.includes(p.cname)) {
                                ids.push(p.cname);
                            }
                            break;
                        }
                    }
                }
            }
        }
        if (isSelfSpeaking.current && !ids.includes(selfCname)) ids.push(selfCname);

        if (hasSetsDiff(store.getState().room.speakerList, new Set(ids))) dispatch(setSpeakerList(ids));
    }, 100);
};

export const useParticipantDisplayName = (participant: MediaParticipant) => {
    const currentMeeting = useCurrentMeeting();

    if (!participant) return "";

    if (participant.name) return participant.name;

    const id = parseSIPUri(participant.cname).identifier;
    const invitation = currentMeeting.getInvitationById(parseInt(id));
    return invitation ? currentMeeting.getDisplayName(invitation) ?? invitation.id.toString() : id;
};
